import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import LegalTooltip from './LegalTooltip'
import LoadingModal from './LoadingModal'
import LabeledInput from './ui/LabeledInput'
import LabeledSelect from './ui/LabeledSelect'
import { Button } from './ui/button'
import { callVincentric } from '@/api/vincentric-client-api'
import { getRecaptchaToken, useRecaptcha } from '@/api/recaptcha-client-api'

const CONDITIONS = [
  { value: 'new', label: 'New' },
  { value: 'used', label: 'Used' },
]

const STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
]

const ANNUAL_MILEAGE_OPTIONS = [
  { value: '10000', label: '10,000' },
  { value: '20000', label: '20,000' },
  { value: '30000', label: '30,000' },
]

export default function VehicleEntryForm() {
  const navigate = useNavigate()
  const [condition, setCondition] = useState('')
  const [year, setYear] = useState('')
  const [make, setMake] = useState('')
  const [model, setModel] = useState('')
  const [trim, setTrim] = useState('')
  const [currentMileage, setCurrentMileage] = useState('')
  const [annualMileage, setAnnualMileage] = useState('')
  const [state, setState] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [triggerValidation, setTriggerValidation] = useState(false)
  
  // Format number with commas
  const formatNumberWithCommas = (value) => {
    // Remove all non-digit characters
    const numericValue = value.replace(/\D/g, '')
    // Add commas for thousands
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }
  
  // Handle current mileage change with formatting
  const handleCurrentMileageChange = (e) => {
    const rawValue = e.target.value
    const formattedValue = formatNumberWithCommas(rawValue)
    setCurrentMileage(formattedValue)
  }
  
  // Use the reCAPTCHA hook
  useRecaptcha()
  
  // API data states
  const [years, setYears] = useState([])
  const [makes, setMakes] = useState([])
  const [models, setModels] = useState([])
  const [trims, setTrims] = useState([])
  
  // Loading states for each field
  const [loadingYears, setLoadingYears] = useState(false)
  const [loadingMakes, setLoadingMakes] = useState(false)
  const [loadingModels, setLoadingModels] = useState(false)
  const [loadingTrims, setLoadingTrims] = useState(false)

  // Custom validation for current mileage
  const validateCurrentMileage = (value) => {
    // Remove commas for validation
    const numericValue = value.replace(/,/g, '')
    const mileage = parseInt(numericValue)
    if (isNaN(mileage)) {
      return { isValid: false, errorMessage: 'Please enter a valid number' }
    }
    if (mileage < 0) {
      return { isValid: false, errorMessage: 'Must be greater than 0 miles.' }
    }
    if (mileage > 330000) {
      return { isValid: false, errorMessage: 'Must be less than 330,000 miles.' }
    }
    
    return { isValid: true, errorMessage: '' }
  }

  const clearYears = () => {
    setYear('')
    setYears([])
    setLoadingYears(false)
  }

  const clearMakes = () => {
    setMake('')
    setMakes([])
    setLoadingMakes(false)
  }

  const clearModels = () => {
    setModel('')
    setModels([])
    setLoadingModels(false)
  }

  const clearTrims = () => {
    setTrim('')
    setTrims([])
    setLoadingTrims(false)
  }

  const fetchYears = useCallback(async () => {
    try {
      setLoadingYears(true)
      const response = await callVincentric('Year', { VehicleOption: condition })
      const yearOptions = response.value.map(item => ({ 
        value: String(item.year), 
        label: String(item.year) 
      })).sort((a, b) => parseInt(b.value) - parseInt(a.value)) // Sort years in descending order (highest to lowest)
      setYears(yearOptions)
    } catch (error) {
      console.error('Error fetching years:', error)
      setYears([])
    } finally {
      setLoadingYears(false)
    }
  }, [condition])

  const fetchMakes = useCallback(async () => {
    try {
      setLoadingMakes(true)
      const response = await callVincentric('Make', { VehicleOption: condition, Year: year })
      const makeOptions = response.value.map(item => ({ value: item.make, label: item.make }))
      setMakes(makeOptions)
    } catch (error) {
      console.error('Error fetching makes:', error)
      setMakes([])
    } finally {
      setLoadingMakes(false)
    }
  }, [condition, year])

  const fetchModels = useCallback(async () => {
    try {
      setLoadingModels(true)
      const response = await callVincentric('Model', { VehicleOption: condition, Year: year, Make: make })
      const modelOptions = response.value.map(item => ({ value: item.model, label: item.model }))
      setModels(modelOptions)
    } catch (error) {
      console.error('Error fetching models:', error)
      setModels([])
    } finally {
      setLoadingModels(false)
    }
  }, [condition, year, make])

  const fetchTrims = useCallback(async () => {
    try {
      setLoadingTrims(true)
      const response = await callVincentric('Trim', { VehicleOption: condition, Year: year, Make: make, Model: model })
      const trimOptions = response.value.map(item => ({ 
        value: item.trim, 
        label: item.trim
      }))
      setTrims(trimOptions)
    } catch (error) {
      console.error('Error fetching trims:', error)
      setTrims([])
    } finally {
      setLoadingTrims(false)
    }
  }, [condition, year, make, model])

  const handleSubmit = async e => {
    e.preventDefault()
    
    // Trigger validation on all fields
    setTriggerValidation(true)
    
    // Check if all required fields are filled
    const allFieldsFilled = condition && year && make && model && trim && currentMileage && annualMileage && state
    
    // Additional validation for current mileage
    const currentMileageValid = validateCurrentMileage(currentMileage, !currentMileage)
    
    if (allFieldsFilled && currentMileageValid.isValid) {
      setIsLoading(true)
      
      try {
        // Get reCAPTCHA token
        const recaptchaToken = await getRecaptchaToken()
        
        // Make the CTO API call with reCAPTCHA token
        const body = {
          VehicleOption: condition === 'new' ? 'New' : 'Used',
          Year: parseInt(year),
          Make: make,
          Model: model,
          Trim: trim,
          BeginningMileage: parseInt(currentMileage.replace(/,/g, '')),
          AnnualMileage: parseInt(annualMileage),
          State: state,
        }
        
        const response = await callVincentric('CTO', body, recaptchaToken)
        console.log('CTO API Response:', response)
        
        // Process the response and navigate to results page
        if (response && response.value && response.value.length > 0) {
          const costToOwn = response.value[0].costToOwn
          
          if (costToOwn && costToOwn.length > 0) {
            // Extract cost data from the first (and typically only) costToOwn entry
            const costData = costToOwn[0]
            
            // Create categories data from the single cost entry
            const categoriesData = {
              'Depreciation': costData.depreciation || 0,
              'Financing': costData.finance || 0,
              'Insurance': costData.insurance || 0,
              'Fuel': costData.fuel || 0,
              'Maintenance': costData.maintenance || 0,
              'Repairs': costData.repair || 0,
              'Taxes & Fees': (costData.fees || 0) + (costData.taxes || 0),
            }
            
            // Navigate to results page with the data and vehicle info
            navigate('/results', { 
              state: { 
                categoriesData,
                vehicleInfo: {
                  year,
                  make,
                  model,
                  trim,
                  condition
                }
              } 
            })
          } else {
            throw new Error('No cost data found in response')
          }
        } else {
          throw new Error('Invalid response format')
        }
        
      } catch (error) {
        console.error('Error during form submission:', error)

        let errorMessage = 'An error occurred. Please try again.'

        if (error.message.includes('reCAPTCHA')) {
          errorMessage = 'Verification failed. Please try again.'
        } else if (error.statusCode === 400) {
          // Handle Vincentric API errors (400 Bad Request)
          if (error.details && error.details.vincentricError) {
            errorMessage = `Vehicle Error: ${error.details.vincentricError}`
          } else {
            errorMessage = error.message || 'Invalid vehicle selection. Please check your inputs and try again.'
          }
        } else if (error.statusCode === 502) {
          errorMessage = 'Service temporarily unavailable. Please try again in a few moments.'
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.'
        } else if (error.message) {
          errorMessage = error.message
        }
        
        alert(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }
    
    // Reset trigger after a short delay to allow validation to run
    setTimeout(() => {
      setTriggerValidation(false)
    }, 100)
  }

  // Fetch years when condition changes
  useEffect(() => {
    if (condition) {
      clearYears()
      clearMakes()
      clearModels()
      clearTrims()

      // Set current mileage to 0 and disable editing when "New" is selected
      if (condition === 'new') {
        setCurrentMileage('0')
      }

      // Refetch years
      fetchYears()
    }
  }, [condition, fetchYears])

  // Fetch makes when year changes
  useEffect(() => {
    if (year) {
      clearMakes()
      clearModels()
      clearTrims()

      // Refetch makes
      fetchMakes()
    }
  }, [year, fetchMakes])

  // Fetch models when make changes
  useEffect(() => {
    if (make) {
      clearModels()
      clearTrims()

      // Refetch models
      fetchModels()
    }
  }, [make, fetchModels])

  // Fetch trims when model changes
  useEffect(() => {
    if (model) {
      clearTrims()

      // Refetch trims
      fetchTrims()
    }
  }, [model, fetchTrims])

  return (
    <>
      <form className="w-full flex flex-col gap-4" onSubmit={handleSubmit}>
        <h2 className="text-[20px] font-semibold font-[600] leading-[32px] text-[#222] mb-2 text-center">What vehicle are you after?</h2>
        <LabeledSelect
          label="Condition*"
          value={condition}
          onChange={setCondition}
          options={CONDITIONS}
          placeholder="Select condition"
          triggerValidation={triggerValidation}
        />
        <LabeledSelect
          label="Year*"
          value={year}
          onChange={setYear}
          options={years}
          placeholder={loadingYears ? "Loading..." : "Select year"}
          disabled={!condition || loadingYears}
          triggerValidation={triggerValidation}
        />
        <div className="flex gap-4">
          <div className="w-1/2">
            <LabeledSelect
              label="Make*"
              value={make}
              onChange={setMake}
              options={makes}
              placeholder={loadingMakes ? "Loading..." : "Select make"}
              disabled={!year || loadingMakes}
              triggerValidation={triggerValidation}
            />
          </div>
          <div className="w-1/2">
            <LabeledSelect
              label="Model*"
              value={model}
              onChange={setModel}
              options={models}
              placeholder={loadingModels ? "Loading..." : "Select model"}
              disabled={!make || loadingModels}
              triggerValidation={triggerValidation}
            />
          </div>
        </div>
        <LabeledSelect
          label="Trim*"
          value={trim}
          onChange={setTrim}
          options={trims}
          placeholder={loadingTrims ? "Loading..." : "Select trim"}
          disabled={!model || loadingTrims}
          triggerValidation={triggerValidation}
        />
        <div className="flex gap-4">
          <div className="w-1/2">
            <LabeledInput
              label="Current Mileage*"
              value={currentMileage}
              onChange={handleCurrentMileageChange}
              placeholder="e.g. 10,000"
              type="text"
              onValidate={validateCurrentMileage}
              triggerValidation={triggerValidation}
              disabled={condition === 'new'}
            />
          </div>
          <div className="w-1/2">
            <LabeledSelect
              label="Annual Mileage*"
              value={annualMileage}
              onChange={setAnnualMileage}
              options={ANNUAL_MILEAGE_OPTIONS}
              placeholder="Select mileage"
              triggerValidation={triggerValidation}
            />
          </div>
        </div>
        <LabeledSelect
          label="State of Vehicle Registration*"
          value={state}
          onChange={setState}
          options={STATES}
          placeholder="Select state"
          triggerValidation={triggerValidation}
        />
        <Button type="submit">
          Show My Cost Breakdown
        </Button>
        <div className="mt-2 text-center">
          <LegalTooltip />
        </div>
      </form>
      <LoadingModal isOpen={isLoading} />
      {/* Hidden reCAPTCHA container */}
      <div id="recaptcha-container" style={{ display: 'none' }}></div>
    </>
  )
}

